import React, {useEffect, useState} from "react";
import {injectIntl} from "react-intl";
import {Box, Chip} from "@mui/material";
import CommonButton from "@/views/CommonComponent/CommonButton";
import TitleBox from "@/views/CommonComponent/TitleBox";
import CommonDialog from "@/views/CommonComponent/CommonDialog";
import BirthDateChangeConsentDialog from "@/views/CommonComponent/BirthDateChangeConsentDialog";
import {
  ButtonContainer,
  ErrorText,
  GenderButton,
  GenderButtonGroup,
  InfoBox,
  InputGroup,
  InputLabelRequired,
  InputLabelText,
  PlainInputField,
  PlainInputRightSlot,
  PlainInputWrapper,
  SignInContentsBox,
  SignInRoot,
} from "../SignIn/Styled/SignInStyled";
import {inject, observer} from "mobx-react";
import {useHistory, useLocation} from "react-router-dom";
import styled from "styled-components";

const ConsentBadge = styled(Chip)`
  margin-left: 8px;
  background-color: #868aff;
  color: white;
  font-size: 12px;
  height: 24px;
`;

const BirthDateHelperText = React.memo(function BirthDateHelperText(props) {
  const { lines } = props;
  return (
    <Box
      component="ul"
      sx={{
        margin: 0,
        marginTop: 0.5,
        listStyleType: "disc",
        paddingLeft: 2.5,
        color: "#8F8F8F",
        fontSize: "0.75rem",
      }}
    >
      {lines.map((line) => (
        <Box component="li" key={line}>
          {line.replace(/^\*\s*/, "")}
        </Box>
      ))}
    </Box>
  );
});

function UserInfoSetting(props) {
  const { intl, authStore, userStore } = props;
  const history = useHistory();
  const location = useLocation();

  const device = userStore.userDeviceDetail;
  const deviceId = device?.deviceId ?? null;

  const mapGenderToUi = (gender) => {
    if (gender === "M") return "boy";
    if (gender === "F") return "girl";
    return "";
  };

  const profileBirthDateStr = device?.profile?.birthDate != null
    ? String(device.profile.birthDate).padStart(8, "0")
    : "";

  const [nickname, setNickname] = useState(
    device?.profile?.name ?? ""
  );

  const [birthDate, setBirthDate] = useState(
    profileBirthDateStr
  );
  const [ageAgreedToUseUnder14, setAgeAgreedToUseUnder14] = useState(
    device?.profile?.isAgreedToUseUnder14
  );
  const [selectedGender, setSelectedGender] = useState(
    mapGenderToUi(device?.profile?.gender)
  );
  const [isMemoryEnabled, setIsMemoryEnabled] = useState(
    device?.profile?.isMemoryDisabled != null ? !device.profile.isMemoryDisabled : true
  );
  const [isWebSearchEnabled, setIsWebSearchEnabled] = useState(
      device?.profile?.isWebSearchDisabled != null ? !device.profile.isWebSearchDisabled : true
  );
  const [isNicknameTouched, setIsNicknameTouched] = useState(false);
  const [showUnsavedDataWarning, setShowUnsavedDataWarning] = useState(false);
  const [showBirthDateChangeConsentDialog, setShowBirthDateChangeConsentDialog] =
    useState(false);
  /** 프로필과 다른 생년월일 입력 시, 안내 팝업에서 '계속 변경'을 눌렀을 때만 true */
  const [birthDateChangeAcknowledged, setBirthDateChangeAcknowledged] =
    useState(false);
  /**
   * '계속 변경' 이후: 입력을 프로필 생년월일과 같게 되돌려도 동의는 복구하지 않으며,
   * 저장 시 보호자 동의 화면이 필요합니다. 보호자 동의 완료 후 돌아오면 false로 초기화.
   */
  const [birthDateChangeFlowCommitted, setBirthDateChangeFlowCommitted] =
    useState(false);
  const hasGuardianConsent =
    ageAgreedToUseUnder14 === true ||
    ageAgreedToUseUnder14 === "true" ||
    authStore.ageAgreedToUseUnder14 === true;

  useEffect(() => {
    if (!deviceId) {
      history.replace("/deviceList");
      return;
    }

    setNickname(device?.profile?.name ?? "");
    setBirthDate(profileBirthDateStr);
    setAgeAgreedToUseUnder14(device?.profile?.isAgreedToUseUnder14);
    setSelectedGender(mapGenderToUi(device?.profile?.gender));
    setIsMemoryEnabled(
      device?.profile?.isMemoryDisabled != null ? !device.profile.isMemoryDisabled : true
    );
    setIsWebSearchEnabled(
        device?.profile?.isWebSearchDisabled != null ? !device.profile.isWebSearchDisabled : true
    );
    setBirthDateChangeAcknowledged(false);
    setBirthDateChangeFlowCommitted(false);
  }, [deviceId]);

  useEffect(() => {
    if (location.state?.fromGuardianConsentReturn) {
      setBirthDateChangeFlowCommitted(false);
    }
    if (location.state?.birthDate !== undefined) {
      setBirthDate(location.state.birthDate);
      setBirthDateChangeAcknowledged(true);
    }
    if (location.state?.ageAgreedToUseUnder14 !== undefined) {
      setAgeAgreedToUseUnder14(location.state.ageAgreedToUseUnder14);
      authStore.setAgeAgreedToUseUnder14(
        location.state.ageAgreedToUseUnder14 === true ||
        location.state.ageAgreedToUseUnder14 === "true"
      );
    }
    if (location.state?.nickname !== undefined) {
      setNickname(location.state.nickname);
    }
    if (location.state?.selectedGender !== undefined) {
      setSelectedGender(location.state.selectedGender);
    }
    if (location.state?.isMemoryEnabled !== undefined) {
      setIsMemoryEnabled(location.state.isMemoryEnabled);
    }
  }, [location.state]);

  const handleBack = () => {
    if (isChangeData()) {
      setShowUnsavedDataWarning(true);
    } else {
      history.goBack();
    }
  };

  const handleUnsavedWarningLeave = () => {
    setShowUnsavedDataWarning(false);
    history.push("/deviceDetail", { deviceId, from: location.state?.from })
  };

  const handleInput = (e) => {
    e.target.value = e.target.value.replace(/[^0-9]/g, "");
  };

  const handleChangeNickname = (e) => {
    const value = e.target.value;
    setIsNicknameTouched(true);
    if (value.length <= 16) {
      setNickname(value);
    }
  };

  const handleBlurNickname = () => {
    setIsNicknameTouched(true);
  };

  const normalizeBirthDate = (date) =>
    String(date ?? "")
      .replace(/[^0-9]/g, "")
      .slice(0, 8);

  const handleChangeBirthDate = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");

    // 생년월일은 숫자 8자리까지만 허용
    if (value.length > 8) {
      return;
    }

    setBirthDate(value);

    const normalizedValue = normalizeBirthDate(value);
    const normalizedProfile = normalizeBirthDate(profileBirthDateStr);
    const hasProfileBirthDate = normalizedProfile.length > 0;

    if (normalizedValue === normalizedProfile) {
      if (!birthDateChangeFlowCommitted) {
        const agreed = device?.profile?.isAgreedToUseUnder14;
        setAgeAgreedToUseUnder14(agreed);
        authStore.setAgeAgreedToUseUnder14(
          agreed === true || agreed === "true"
        );
      } else {
        setAgeAgreedToUseUnder14(false);
        authStore.setAgeAgreedToUseUnder14(false);
      }
      setBirthDateChangeAcknowledged(false);
    } else if (birthDateChangeAcknowledged) {
      setAgeAgreedToUseUnder14(false);
      authStore.setAgeAgreedToUseUnder14(false);
    }

    setShowBirthDateChangeConsentDialog(
      hasProfileBirthDate &&
      normalizedValue !== normalizedProfile &&
      !birthDateChangeAcknowledged
    );
  };

  const handleSelectBoy = () => {
    setSelectedGender((prevGender) => (prevGender === "boy" ? "" : "boy"));
  };

  const handleSelectGirl = () => {
    setSelectedGender((prevGender) => (prevGender === "girl" ? "" : "girl"));
  };

  const isBirthDateChangedFromProfile = () =>
    normalizeBirthDate(profileBirthDateStr) !== normalizeBirthDate(birthDate);

  const goToGuardianConsentFromSettings = () => {
    if (isUnder14(birthDate)) {
      authStore.setAgeOver14(false);
    } else {
      authStore.setAgeOver14(true);
    }
    authStore.setNickname(nickname);
    authStore.setBirthDate(birthDate);
    authStore.setGender(selectedGender);
    history.push("/signin/guardian-consent", {
      from: "UserInfoSetting",
      deviceId,
      birthDate,
      nickname,
      selectedGender,
      isMemoryEnabled,
    });
  };

  const handleCancelBirthDateChangeConsent = () => {
    setShowBirthDateChangeConsentDialog(false);
    setBirthDateChangeAcknowledged(false);
    setBirthDateChangeFlowCommitted(false);
    setBirthDate(profileBirthDateStr);
    const agreed = device?.profile?.isAgreedToUseUnder14;
    setAgeAgreedToUseUnder14(agreed);
    authStore.setAgeAgreedToUseUnder14(
      agreed === true || agreed === "true"
    );
  };

  const handleContinueBirthDateChangeConsent = () => {
    setBirthDateChangeFlowCommitted(true);
    setBirthDateChangeAcknowledged(true);
    setAgeAgreedToUseUnder14(false);
    authStore.setAgeAgreedToUseUnder14(false);
    setShowBirthDateChangeConsentDialog(false);
  };

  const handleClick = () => {
    if (
      birthDate &&
      isBirthDateValid(birthDate) &&
      !hasGuardianConsent &&
      (isUnder14(birthDate) ||
        isBirthDateChangedFromProfile() ||
        birthDateChangeFlowCommitted)
    ) {
      goToGuardianConsentFromSettings();
      return;
    }

    const profileHadConsent =
      device?.profile?.isAgreedToUseUnder14 === true ||
      device?.profile?.isAgreedToUseUnder14 === "true";

    const agreedToSave =
      isBirthDateChangedFromProfile() || birthDateChangeFlowCommitted
        ? hasGuardianConsent === true
        : hasGuardianConsent === true || profileHadConsent;

    authStore.setNickname(nickname);
    authStore.setBirthDate(birthDate);
    authStore.setGender(selectedGender);
    authStore.setAgeAgreedToUseUnder14(agreedToSave);
    authStore.setIsMemoryEnabled(isMemoryEnabled === true);
    authStore.setIsWebSearchEnabled(isWebSearchEnabled === true);

    const goAfterSave = () => {
      userStore.getUserDevice(authStore.loginUser.id, deviceId, () => {
        history.push("/deviceDetail", { deviceId, from: location.state?.from });
      });
    };

    authStore.updateUserDeviceProfile(deviceId, goAfterSave);
  };

  const isChangeData = () => {
    const originalName = device?.profile?.name ?? "";
    const originalBirthDate = profileBirthDateStr;
    const originalGender = mapGenderToUi(device?.profile?.gender);

    if ((originalName ?? "") !== (nickname ?? "")) return true;
    if ((originalBirthDate ?? "") !== (birthDate ?? "")) return true;
    if ((originalGender ?? "") !== (selectedGender ?? "")) return true;
    if (birthDateChangeFlowCommitted) return true;
    return false;
  };

  const isBirthDateValid = (date) => {
    if (date === '') {
      return false;
    }

    if (typeof date === 'number') {
      date = date.toString();
    }
    // 정규 표현식으로 기본 형식 및 년도 범위 검사 (1900-1999, 2000-2099)
    if (!/^(19\d{2}|20\d{2})(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])$/.test(date)) {
      return false;
    }

    // 연도, 월, 일을 분리
    const year = parseInt(date.substring(0, 4), 10);
    const month = parseInt(date.substring(4, 6), 10) - 1; // 월은 0부터 시작
    const day = parseInt(date.substring(6, 8), 10);

    // Date 객체를 사용하여 유효한 날짜인지 확인
    const birthDate = new Date(year, month, day);
    return (
        birthDate.getFullYear() === year &&
        birthDate.getMonth() === month &&
        birthDate.getDate() === day
    );
  };

  const isButtonDisabled = () => {
    return (
      (authStore.isNotAcceptableNickname && authStore.isNotAcceptableNickname(intl, nickname) !== "") ||
      nickname.trim() === "" ||
      !isBirthDateValid(birthDate) ||
      showBirthDateChangeConsentDialog
    );
  };

  const isUnder14 = (birthDateStr) => {
    if (!birthDateStr || birthDateStr.length !== 8) return false;
    const year = parseInt(birthDateStr.substring(0, 4));
    const month = parseInt(birthDateStr.substring(4, 6));
    const day = parseInt(birthDateStr.substring(6, 8));
    const birth = new Date(year, month - 1, day);
    const today = new Date();
    const age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birth.getDate())
    ) {
      return age - 1 < 14;
    }
    return age < 14;
  };

  const birthDateHelperLines = intl
    .formatMessage({ id: "birthDate_note" })
    .split("\n")
    .filter(Boolean);

  const nicknameValidationMessage = authStore.isNotAcceptableNickname
    ? (nickname ?? "").trim() === ""
      ? ""
      : authStore.isNotAcceptableNickname(intl, nickname)
    : "";
  const showNicknameError =
    isNicknameTouched &&
    (nicknameValidationMessage !== "");

  const guardianConsentBadgeMessageId =
    birthDate && isBirthDateValid(birthDate) && !isUnder14(birthDate)
      ? "guardian_consent_complete_badge_over14"
      : "guardian_consent_complete_badge";

  return (
    <SignInRoot>
      <TitleBox
        title={intl.formatMessage({ id: "user_info_change" })}
        subtitle={intl.formatMessage({ id: "userInfoSetting_text" })}
        showBackButton
        onBack={handleBack}
      />
      <SignInContentsBox className="modify">
        <InfoBox mt={1}>
          <InputGroup>
            <InputLabelText>
              {intl.formatMessage({ id: "name_label" })}
              <InputLabelRequired>*</InputLabelRequired>
            </InputLabelText>
            <PlainInputField
              placeholder={intl.formatMessage({ id: "name_placeholder" })}
              value={nickname}
              onChange={handleChangeNickname}
              onBlur={handleBlurNickname}
              autoComplete="off"
              hasError={showNicknameError}
            />
            {showNicknameError && (
              <ErrorText>
                {nicknameValidationMessage ||
                  intl.formatMessage({ id: "name_error" })}
              </ErrorText>
            )}
          </InputGroup>

          <InputGroup>
            <InputLabelText>
              {intl.formatMessage({ id: "birthDate_label" })}
              <InputLabelRequired>*</InputLabelRequired>
            </InputLabelText>
            <PlainInputWrapper>
              <PlainInputField
                type="tel"
                onInput={handleInput}
                placeholder={intl.formatMessage({ id: "birthDate_placeholder" })}
                value={birthDate}
                onChange={handleChangeBirthDate}
                autoComplete="off"
                hasError={birthDate && !isBirthDateValid(birthDate)}
              />
              {hasGuardianConsent && (
                <PlainInputRightSlot>
                  <ConsentBadge
                    label={intl.formatMessage({
                      id: guardianConsentBadgeMessageId,
                    })}
                    size="small"
                  />
                </PlainInputRightSlot>
              )}
            </PlainInputWrapper>
            <BirthDateHelperText lines={birthDateHelperLines} />
          </InputGroup>

          <InputGroup>
            <InputLabelText>
              {intl.formatMessage({ id: "gender_label", defaultMessage: "성별 (선택)" })}
            </InputLabelText>
            <GenderButtonGroup>
              <GenderButton
                type="button"
                isSelected={selectedGender === "boy"}
                onClick={handleSelectBoy}
              >
                {intl.formatMessage({ id: "gender_boy" })}
              </GenderButton>
              <GenderButton
                type="button"
                isSelected={selectedGender === "girl"}
                onClick={handleSelectGirl}
              >
                {intl.formatMessage({ id: "gender_girl" })}
              </GenderButton>
            </GenderButtonGroup>
          </InputGroup>
        </InfoBox>

        <ButtonContainer>
          <CommonButton
            text={intl.formatMessage({
              id: "user_info_save",
              defaultMessage: "저장하기",
            })}
            background="#FDD751"
            color="#333"
            disabled={isButtonDisabled()}
            onClick={handleClick}
          />
        </ButtonContainer>
      </SignInContentsBox>

      <CommonDialog
        description="미저장 데이터 경고"
        open={showUnsavedDataWarning}
        title={intl.formatMessage({ id: "user_info_unsaved_warning_title" })}
        subText={intl.formatMessage({ id: "user_info_unsaved_warning_text" })}
        onClick1={handleUnsavedWarningLeave}
        onClick2={() => setShowUnsavedDataWarning(false)}
        LeftButtonText={intl.formatMessage({ id: "exit_page" })}
        RightButtonText={intl.formatMessage({ id: "dialog_NoButton" })}
      />

      <BirthDateChangeConsentDialog
        open={showBirthDateChangeConsentDialog}
        title={intl.formatMessage({ id: "birthdate_change_guardian_title" })}
        body={intl.formatMessage({ id: "birthdate_change_guardian_body" })}
        cancelLabel={intl.formatMessage({ id: "dialog_cancle" })}
        continueLabel={intl.formatMessage({
          id: "birthdate_change_guardian_continue",
        })}
        onCancel={handleCancelBirthDateChangeConsent}
        onContinue={handleContinueBirthDateChangeConsent}
      />
    </SignInRoot>
  );
}

export default injectIntl(
  inject("authStore", "userStore")(observer(UserInfoSetting))
);
